/**
 * Single per-session WebSocket to `/ws/realtime` that drives TanStack Query
 * cache invalidation from server-side push, replacing previous polling on
 * notifications/parcels.
 *
 * - Auth: JWT access token is appended as the `?token=` query string
 *   (matches `utils/ws_auth.py::JWTAuthMiddleware`, same as the support chat).
 * - Reconnect: exponential backoff (1s → 2s → … → 30s), reset on every
 *   successful open. Also wakes immediately on `online` / `visibilitychange`.
 * - Inbound payload shape: `{type: 'invalidate', query_key: 'notifications' | 'parcels'}`.
 *   Anything else is ignored.
 *
 * Staff JWTs (`type='staff'`) are accepted by the backend consumer and
 * placed into a shared `realtime.staff` group, so the admin UI receives
 * fan-outs like `cargo-ids` whenever any client posts a new CargoId
 * request.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from '@/shared/lib/auth';
import { getWsOrigin, env } from '@/shared/lib/env';
import { queryKeys } from '@/shared/api/queryKeys';

type InvalidatePayload = {
  type: 'invalidate';
  query_key: string;
};

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** Server-side auth/protocol close codes — do not retry on these. */
const AUTH_CLOSE_CODES = new Set([4401, 4001, 4003]);

const DEFAULT_REALTIME_URL = 'wss://api.tezcargojapan.uz/ws/realtime';

// Admin lookup map. `parcels` and `cargo-ids` are dispatched to the shared
// `realtime.staff` group so the lists/queues refresh without polling.
// A value may be a single key or several keys (e.g. `splits` refreshes both
// the list and the sidebar pending-count badge).
const INVALIDATE_MAP: Record<string, QueryKey | QueryKey[]> = {
  // Parcel / split / return changes move PINFL customs-limit usage, so they
  // also refresh every cached PINFL limit.
  parcels: [queryKeys.parcels.all, queryKeys.pinfl.all],
  'cargo-ids': queryKeys.cargoIds.all,
  splits: [queryKeys.splits.all, queryKeys.splits.pendingCount, queryKeys.pinfl.all],
  reports: [queryKeys.reports.all, queryKeys.reports.pendingCount],
  returns: [queryKeys.returns.all, queryKeys.returns.pendingCount, queryKeys.pinfl.all],
  pinfl: queryKeys.pinfl.all,
};

const resolveBaseUrl = (): string => {
  const fromEnv = import.meta.env.VITE_WS_URL as string | undefined;
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  const origin = getWsOrigin(env.apiUrl);
  if (origin) return `${origin}/ws/realtime`;
  return DEFAULT_REALTIME_URL;
};

const buildSocketUrl = (token: string): string => {
  const base = resolveBaseUrl();
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(token)}`;
};

type Conn = {
  socket: WebSocket | null;
  backoff: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  stopped: boolean;
};

let conn: Conn | null = null;

const clearReconnect = (c: Conn): void => {
  if (c.reconnectTimer !== null) {
    clearTimeout(c.reconnectTimer);
    c.reconnectTimer = null;
  }
};

const handleInvalidate = (qc: QueryClient, payload: InvalidatePayload): void => {
  const mapped = INVALIDATE_MAP[payload.query_key];
  if (!mapped) return;
  const keys = Array.isArray(mapped[0]) ? (mapped as QueryKey[]) : [mapped as QueryKey];
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: key });
  }
};

const openSocket = (qc: QueryClient, c: Conn): void => {
  if (c.stopped) return;
  const token = getAccessToken();
  if (!token) {
    scheduleReconnect(qc, c);
    return;
  }
  const url = buildSocketUrl(token);
  const ws = new WebSocket(url);
  c.socket = ws;

  ws.onopen = () => {
    c.backoff = INITIAL_BACKOFF_MS;
  };

  ws.onmessage = (event) => {
    let data: unknown;
    try {
      data = JSON.parse(event.data as string);
    } catch {
      return;
    }
    if (!data || typeof data !== 'object') return;
    const payload = data as { type?: string; query_key?: string };
    if (payload.type === 'invalidate' && typeof payload.query_key === 'string') {
      handleInvalidate(qc, payload as InvalidatePayload);
    }
  };

  ws.onclose = (event) => {
    if (c.socket === ws) c.socket = null;
    if (c.stopped) return;
    if (AUTH_CLOSE_CODES.has(event.code)) return;
    scheduleReconnect(qc, c);
  };

  ws.onerror = () => {
    try {
      ws.close();
    } catch {
      /* noop */
    }
  };
};

const scheduleReconnect = (qc: QueryClient, c: Conn): void => {
  if (c.stopped || c.reconnectTimer !== null) return;
  const delay = c.backoff;
  c.backoff = Math.min(c.backoff * 2, MAX_BACKOFF_MS);
  c.reconnectTimer = setTimeout(() => {
    c.reconnectTimer = null;
    openSocket(qc, c);
  }, delay);
};

const wakeIfIdle = (qc: QueryClient): void => {
  const c = conn;
  if (!c || c.stopped) return;
  const live =
    c.socket !== null &&
    (c.socket.readyState === WebSocket.OPEN ||
      c.socket.readyState === WebSocket.CONNECTING);
  if (live) return;
  clearReconnect(c);
  c.backoff = INITIAL_BACKOFF_MS;
  openSocket(qc, c);
};

const onOnline = (qc: QueryClient) => (): void => wakeIfIdle(qc);
const onVisibility = (qc: QueryClient) => (): void => {
  if (document.visibilityState === 'visible') wakeIfIdle(qc);
};

type RealtimeHandle = {
  disconnect: () => void;
};

let onlineHandler: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;

/**
 * Open the realtime socket. Idempotent: a second call is a no-op while the
 * existing socket is still alive. Returns a handle whose `disconnect` tears
 * the connection down (use during logout / token refresh).
 */
export const connectRealtime = (qc: QueryClient): RealtimeHandle => {
  if (conn && !conn.stopped) {
    return { disconnect: disconnectRealtime };
  }
  conn = {
    socket: null,
    backoff: INITIAL_BACKOFF_MS,
    reconnectTimer: null,
    stopped: false,
  };

  onlineHandler = onOnline(qc);
  visibilityHandler = onVisibility(qc);
  window.addEventListener('online', onlineHandler);
  document.addEventListener('visibilitychange', visibilityHandler);

  openSocket(qc, conn);

  return { disconnect: disconnectRealtime };
};

export const disconnectRealtime = (): void => {
  const c = conn;
  if (!c) return;
  c.stopped = true;
  clearReconnect(c);
  if (c.socket) {
    c.socket.onopen = null;
    c.socket.onmessage = null;
    c.socket.onclose = null;
    c.socket.onerror = null;
    try {
      c.socket.close();
    } catch {
      /* noop */
    }
    c.socket = null;
  }
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  conn = null;
};

type ProviderProps = { children: ReactNode };

/**
 * Mount-once provider that wires the realtime socket to the active
 * QueryClient. Place inside the QueryProvider tree near the app root.
 */
export const RealtimeProvider = ({ children }: ProviderProps) => {
  const qc = useQueryClient();
  useEffect(() => {
    const handle = connectRealtime(qc);
    return () => {
      handle.disconnect();
    };
  }, [qc]);
  return <>{children}</>;
};
